import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { executeRecaptcha } from '@ekhodealer/ekho-common';
import { Button, Dropdown, Stack, TextInput } from '@ekhodealer/ekho-common/rev';

import { paths } from '@config/paths';

import { useCreateOnboardingRecord } from '../api/useCreateOnboardingRecord';
import {
	dealerInfoAtom,
	onboardingRecordIdAtom,
	onboardingStepAtom,
} from '../atoms/onboardingAtoms';

import './OnboardingSteps.scss';

/** reCAPTCHA site key — same env var used by authService for password resets, email verification, etc. */
function getRecaptchaSiteKey(): string {
	const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
	if (!siteKey) {
		throw new Error('VITE_RECAPTCHA_SITE_KEY is not configured');
	}
	return siteKey;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const URL_REGEX = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([/?#].*)?$/i;

function validateEmail(value: string): string | false {
	const trimmed = value.trim();
	if (!trimmed) return 'Email address is required.';
	if (!EMAIL_REGEX.test(trimmed)) return 'Please enter a valid email address.';
	return false;
}

function validateWebsiteUrl(value: string): string | false {
	const trimmed = value.trim();
	if (!trimmed) return 'Website URL is required.';
	if (!URL_REGEX.test(trimmed)) return 'Please enter a valid website URL (e.g. www.example.com).';
	return false;
}

/**
 * Step 1 — Dealer Info (public, no auth required)
 *
 * Collects dealership name, website URL, and email.
 * On submit: creates an OnboardingRecord (with email dedup on the backend).
 *   - 409 → email tied to existing Ekho account, directs dealer to Path 2.
 *   - 201 / 200 → record created (or idempotent return), advance to products.
 *
 * reCAPTCHA Enterprise token is sent as `X-Recaptcha-Token` header,
 * validated by the `validateRecaptcha('create_onboarding_record')` middleware on the backend.
 * Pattern mirrors `authService.sendPasswordResetEmail` / `sendEmailVerificationEmail`.
 */
// eslint-disable-next-line import/no-default-export -- matches pattern of all other onboarding step components
export default function OnboardingDealerInfoStep(): React.JSX.Element {
	const navigate = useNavigate();
	const setStep = useSetAtom(onboardingStepAtom);
	const setRecordId = useSetAtom(onboardingRecordIdAtom);

	// Dealer info persisted in Jotai so values survive back/forth navigation
	const [dealerInfo, setDealerInfo] = useAtom(dealerInfoAtom);
	const { dealershipName, websiteUrl, email, productType } = dealerInfo;

	const setDealershipName = useCallback(
		(v: string) => setDealerInfo((prev) => ({ ...prev, dealershipName: v })),
		[setDealerInfo],
	);
	const setWebsiteUrl = useCallback(
		(v: string) => setDealerInfo((prev) => ({ ...prev, websiteUrl: v })),
		[setDealerInfo],
	);
	const setEmail = useCallback(
		(v: string) => setDealerInfo((prev) => ({ ...prev, email: v })),
		[setDealerInfo],
	);
	const setProductType = useCallback(
		(v: string) => setDealerInfo((prev) => ({ ...prev, productType: v as '' | 'access' | 'dealer' })),
		[setDealerInfo],
	);

	const [formError, setFormError] = useState<string | null>(null);
	const [websiteError, setWebsiteError] = useState<string | false>(false);
	const [emailError, setEmailError] = useState<string | false>(false);
	const [productTypeError, setProductTypeError] = useState<string | false>(false);

	const createRecord = useCreateOnboardingRecord();

	// Set step index on mount
	useEffect(() => {
		setStep(0);
	}, [setStep]);

	const handleSubmit = useCallback(async () => {
		setFormError(null);

		// Per-field validation
		const urlErr = validateWebsiteUrl(websiteUrl);
		const emlErr = validateEmail(email);
		const ptErr = productType ? false : 'Please select a business type.';
		setWebsiteError(urlErr);
		setEmailError(emlErr);
		setProductTypeError(ptErr);

		if (!dealershipName.trim()) {
			setFormError('Dealership name is required.');
			return;
		}
		if (urlErr || emlErr || ptErr) return;

		try {
			// Execute reCAPTCHA Enterprise challenge before submitting.
			// This uses the same pattern as authService.sendPasswordResetEmail:
			// 1. Call grecaptcha.enterprise.execute() to get a token
			// 2. Pass token via X-Recaptcha-Token header
			// 3. Backend validateRecaptcha middleware verifies it
			const recaptchaToken = await executeRecaptcha(
				getRecaptchaSiteKey(),
				'create_onboarding_record',
			);

			const record = await createRecord.mutateAsync({
				dealershipName: dealershipName.trim(),
				dealerWebsiteUrl: websiteUrl.trim(),
				email: email.trim().toLowerCase(),
				productType: productType as 'access' | 'dealer',
				recaptchaToken,
			});

			if (record?.id) {
				setRecordId(record.id);
				navigate(paths.onboardingProducts.getHref(), { replace: true });
			} else {
				setFormError('Failed to create onboarding record. Please try again.');
			}
		} catch (err: unknown) {
			// Handle reCAPTCHA errors
			if (err instanceof Error && err.message.includes('reCAPTCHA')) {
				setFormError('reCAPTCHA verification failed. Please try again.');
				return;
			}

			// apiClient.normalizeError flattens AxiosErrors to { message, status, code, details }
			const error = err as { message?: string; status?: number };
			if (error?.status === 409) {
				setFormError(
					error.message ||
					'This email is already associated with an Ekho account. ' +
					'Please log in to your Admin Portal to subscribe to new products.',
				);
			} else if (error?.status === 429) {
				setFormError('Too many requests. Please wait a few minutes and try again.');
			} else if (error?.status === 403) {
				// reCAPTCHA rejected by backend
				setFormError('Security verification failed. Please refresh the page and try again.');
			} else {
				setFormError(
					error?.message ||
					'Something went wrong. Please check your information and try again.',
				);
			}
		}
	}, [dealershipName, websiteUrl, email, productType, createRecord, setRecordId, navigate]);

	return (
		<div className='onboarding-step'>
			<div className='onboarding-step__header'>
				<h2>Get started with Ekho</h2>
				<p>Tell us about your dealership to begin setting up your AI products.</p>
			</div>

			<form className='onboarding-step__form' onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }}>
			<Stack itemsSpacing='16'>
				<TextInput
					label='Dealership Name'
					value={dealershipName}
					onChange={setDealershipName}
					placeholder='e.g. Acme Motors'
					size='large'
					variant='outline'
					isRequired
				/>
				<TextInput
					label='Website URL'
					value={websiteUrl}
					onChange={(v: string) => { setWebsiteUrl(v); if (websiteError) setWebsiteError(false); }}
					onBlur={() => { if (websiteUrl.trim()) setWebsiteError(validateWebsiteUrl(websiteUrl)); }}
					placeholder='e.g. www.acmemotors.com'
					hasError={websiteError || false}
					size='large'
					variant='outline'
					isRequired
				/>
				<TextInput
					label='Email Address'
					value={email}
					onChange={(v: string) => { setEmail(v); if (emailError) setEmailError(false); }}
					onBlur={() => { if (email.trim()) setEmailError(validateEmail(email)); }}
					placeholder='e.g. owner@acmemotors.com'
					type='email'
					hasError={emailError || false}
					size='large'
					variant='outline'
					isRequired
				/>
				<Dropdown
					label='Business type'
					isRequired
					options={[
						{ id: 'dealer', label: 'Dealer' },
						{ id: 'access', label: 'OEM' },
					]}
					value={productType}
					onSelectionChange={(v: string) => { setProductType(v); if (productTypeError) setProductTypeError(false); }}
					placeholder='Select business type'
					size='large'
					hasError={productTypeError || false}
				/>
			</Stack>

			{formError && (
				<div className='onboarding-step__payment-error'>
					{formError}
				</div>
			)}

			<div className='onboarding-step__button-row'>
				<Button
					type='submit'
					variant='filled'
					isDisabled={createRecord.isPending}>
					{createRecord.isPending ? 'Creating...' : 'Continue'}
				</Button>
			</div>
			</form>
		</div>
	);
}
